import type { GridColumnState, GridStatePort } from "./types";
import { GRID_TREE_COLUMN_ID } from "./types";

/**
 * Whether grouping, not the user, hides this column: one being grouped by, or the one the Group
 * column stands in for. Such a column keeps its saved visibility.
 */
export function isHiddenByRowGrouping(
  port: Pick<GridStatePort, "getColumnState" | "getGridOption">,
  column: GridColumnState,
): boolean {
  return !!column.rowGroup || rowGroupStandInColumnId(port) === column.colId;
}

/** The column the Group column stands in for while rows are grouped; undefined otherwise. */
export function rowGroupStandInColumnId(
  port: Pick<GridStatePort, "getColumnState" | "getGridOption">,
): string | undefined {
  if (!port.getColumnState().some((state) => state.rowGroup)) return undefined;
  return port.getGridOption("autoGroupColumnDef")?.field;
}

/** The grid's sorted columns in sort order; the Group column's sort is that of the column it stands in for. */
export function gridSorting(
  port: Pick<GridStatePort, "getColumnState" | "getGridOption">,
): { id: string; desc: boolean }[] {
  const standIn = rowGroupStandInColumnId(port);
  return port
    .getColumnState()
    .filter((col) => col.sort != null)
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map((col) => ({
      id: col.colId === GRID_TREE_COLUMN_ID && standIn ? standIn : col.colId,
      desc: col.sort === "desc",
    }));
}

function scalarKey(value: unknown): string | null {
  if (typeof value === "string") return value === "" ? null : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return value != null && typeof value === "object" ? JSON.stringify(value) : null;
}

/**
 * The default group key: a plain value is its own key. An array forms one group per combination of
 * its entries, independent of their order; null, an empty string or an empty array has none.
 */
export function plainRowGroupKey(value: unknown): string | null {
  if (!Array.isArray(value)) return scalarKey(value);
  const keys = value.map(scalarKey).filter((key): key is string => key !== null);
  return keys.length > 0 ? [...new Set(keys)].sort().join("|") : null;
}
